import Axios from '../../utils/axios';
import { getBackendErrorMessage } from '../../utils/errorHandler';

export enum PitstopType {
  HUB = 'HUB',
  PROVIDER_WAREHOUSE = 'PROVIDER_WAREHOUSE',
}

export interface PitstopCity {
  id: number;
  uuid?: string;
  name: string;
  code: string | null;
  province_id?: number;
  is_active?: boolean;
  created_at?: string;
  timezone?: string;
  google_place_id?: string | null;
  wilayahid_code?: string | null;
}

/** Owning client/provider snapshot on a `PROVIDER_WAREHOUSE` pitstop. */
export interface PitstopProvider {
  id?: number;
  uuid?: string;
  name?: string;
  code?: string;
}

export interface Pitstop {
  id: number;
  uuid: string;
  name: string;
  code: string;
  address: string | null;
  city: PitstopCity | null;
  provider: PitstopProvider | null;
  phoneNumber: string | null;
  latitude: number;
  longitude: number;
  clockInRadius: number;
  coverageRadius: number;
  pitstopType: PitstopType;
  hasStaff: boolean;
  geoHash: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PitstopListEnvelope {
  status: number;
  message?: string;
  data: Pitstop[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
    last_page?: number;
  };
}

export interface CreatePitstopPayload {
  name: string;
  code?: string;
  address: string;
  latitude: number;
  longitude: number;
  pitstopType: PitstopType;
  clockInRadius: number;
  coverageRadius?: number;
  phoneNumber?: string | null;
  cityId?: number;
  hasStaff?: boolean;
  providerID?: number | null;
  tags?: string[];
}

export type UpdatePitstopPayload = Partial<CreatePitstopPayload>;

export const getListPitstop = async ({
  providerId,
  ...params
}: {
  page?: string;
  size?: string;
  search?: string;
  type?: string;
  hasStaff?: boolean;
  /** Filter by owning client/provider — used by the client-pitstop pickers. */
  providerId?: number | string;
}): Promise<PitstopListEnvelope> => {
  try {
    // Core spells this one `providerID` (capital ID) — its `getPitstops`
    // handler destructures `req.query.providerID`. camelCase doesn't error,
    // it just comes back UNFILTERED with every provider's warehouses, so the
    // callers had to re-filter client-side to stay correct. Kept camelCase on
    // this side to match the rest of the FE and mapped here at the boundary.
    const response = await Axios.get('/v1/pitstop', {
      params: {
        ...params,
        ...(providerId !== undefined && providerId !== ''
          ? { providerID: providerId }
          : {}),
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * One pitstop by id, raw (keeps `provider` and `pitstopType`, which the list
 * mapping drops). Used to fetch a location the paginated list didn't reach —
 * a deep-linked `?hub=`, or a hub that already has zones drawn.
 */
export const getPitstopById = async (id: number): Promise<Pitstop> => {
  try {
    const response = await Axios.get(`/v1/pitstop/${id}`);
    return (response.data?.data ?? response.data) as Pitstop;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** Option shape consumed by the searchable hub `<SearchSelect>` dropdowns. */
export interface HubOption {
  id: string;
  name: string;
  code: string;
  /**
   * Owning client's name — set only for client pitstops (PROVIDER_WAREHOUSE),
   * so pickers that list every location can tell them apart from Dash hubs.
   */
  clientName?: string;
  /** Owning client's id — set only for client pitstops, like `clientName`. */
  clientId?: number;
  /** Staffed Dash hub (true) vs unstaffed / client pitstop (false). */
  hasStaff?: boolean;
  /**
   * Which pitstop list the option came from. Set by fetchers that query each
   * type separately, so pickers can group by type instead of inferring it
   * from `hasStaff` (a HUB can be unstaffed).
   */
  pitstopType?: PitstopType;
  /**
   * Master-data contact of the hub, set by `searchHubOptions` — documents that
   * ship to a hub (WMS transfer warehouse) prefill their destination from it.
   */
  address?: string | null;
  phoneNumber?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

/** Pitstop → picker option. `type` is the list the row was fetched from. */
const toHubOption = (p: Pitstop, type?: PitstopType): HubOption => ({
  id: String(p.id),
  name: p.name,
  code: p.code,
  hasStaff: p.hasStaff,
  ...(type ? { pitstopType: type } : {}),
  ...(p.provider?.name ? { clientName: p.provider.name } : {}),
  ...(p.provider?.id != null ? { clientId: p.provider.id } : {}),
});

/**
 * Server-side (`?search=`) HUB lookup for the filter dropdowns. Stable
 * module-level fn so it can be passed straight to `useSearchableOptions`
 * without re-triggering its effect each render.
 */
/**
 * Server-side (`?search=`) lookup across ALL pitstops — Dash hubs AND client
 * pitstops (PROVIDER_WAREHOUSE). Dispatches can depart from either, so the
 * dispatch-side filters must offer both; `searchHubOptions` below stays
 * hub-only for contexts that really mean staffed hubs.
 */
export const searchAllPitstopOptions = async (
  search?: string
): Promise<HubOption[]> => {
  const res = await getListPitstop({
    page: '1',
    size: '100',
    search: search?.trim() || undefined,
  });
  return (res.data ?? []).map((p) => toHubOption(p, p.pitstopType));
};

/**
 * Dispatch-origin lookup — TWO type-scoped calls instead of one mixed page,
 * so both sections of the picker are always populated:
 *   1. `?type=HUB&hasStaff=true&size=50`  — staffed Dash hubs (shown first)
 *   2. `?type=PROVIDER_WAREHOUSE&size=100` — client pitstops (never staffed)
 *
 * The single untyped `size=100` page it replaces could return 100 rows of one
 * type and leave the other section empty. `pitstopType` is carried on each
 * option so the picker groups by the list a row came from rather than
 * guessing from `hasStaff`.
 */
export const searchDispatchOriginOptions = async (
  search?: string
): Promise<HubOption[]> => {
  const term = search?.trim() || undefined;
  const [hubRes, clientRes] = await Promise.all([
    getListPitstop({
      type: PitstopType.HUB,
      hasStaff: true,
      page: '1',
      size: '50',
      search: term,
    }),
    getListPitstop({
      type: PitstopType.PROVIDER_WAREHOUSE,
      page: '1',
      size: '100',
      search: term,
    }),
  ]);
  return [
    ...(hubRes.data ?? []).map((p) => toHubOption(p, PitstopType.HUB)),
    ...(clientRes.data ?? []).map((p) =>
      toHubOption(p, PitstopType.PROVIDER_WAREHOUSE)
    ),
  ];
};

/** A coordinate from the master data (a number or numeric string), else null. */
const coordOf = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const searchHubOptions = async (
  search?: string
): Promise<HubOption[]> => {
  const res = await getListPitstop({
    type: 'HUB',
    hasStaff: true,
    page: '1',
    size: '100',
    search: search?.trim() || undefined,
  });
  return (res.data ?? []).map((p) => ({
    id: String(p.id),
    name: p.name,
    code: p.code,
    address: p.address ?? null,
    phoneNumber: p.phoneNumber ?? null,
    latitude: coordOf(p.latitude),
    longitude: coordOf(p.longitude),
  }));
};
